import type { components } from "@/types/api.generated";
import { detailCode } from "@/lib/api-error";
import apiClient from "./client";

/**
 * Cliente da Calibragem comercial (EF-FUNC-012), com aliases finos sobre os
 * tipos gerados. O contrato comentado está em
 * `docs/superpowers/specs/2026-10-28-calibragem-comercial-contrato-design.md`.
 */
export type CommercialCalibration = components["schemas"]["CommercialCalibrationResponse"];
export type CommercialCalibrationVersion = components["schemas"]["CommercialCalibrationVersion"];
export type CommercialCalibrationRequest = components["schemas"]["CommercialCalibrationIn"];
export type CommercialPreviewRequest = components["schemas"]["CommercialPreviewIn"];
export type CommercialPreview = components["schemas"]["CommercialPreviewOut"];
export type CommercialParameters = components["schemas"]["CommercialParameters"];
export type CommercialScenarioInput = components["schemas"]["CommercialScenarioIn"];
export type CommercialScenarioResult = components["schemas"]["CommercialScenarioResult"];
export type CommercialValidationIssue = components["schemas"]["CommercialValidationIssue"];
type CommercialValidationError = components["schemas"]["CommercialValidationError"];

/** A última versão salva, a base de hoje e se ela mudou. Só `sales` e `admin`. */
export async function getCommercialCalibration(projectId: string): Promise<CommercialCalibration> {
  const { data } = await apiClient.get<CommercialCalibration>(
    `/api/projects/${projectId}/commercial-calibration`,
  );
  return data;
}

/**
 * A conta dos cenários enquanto se digita. **Não grava nada**: é o mesmo motor
 * da gravação, e o que ela devolve é o que salvar grava.
 */
export async function previewCommercialCalibration(
  projectId: string,
  body: CommercialPreviewRequest,
): Promise<CommercialPreview> {
  const { data } = await apiClient.post<CommercialPreview>(
    `/api/projects/${projectId}/commercial-calibration/preview`,
    body,
  );
  return data;
}

/** Grava a versão seguinte. 409 com `detail.code`; 422 com os erros campo a campo. */
export async function saveCommercialCalibration(
  projectId: string,
  body: CommercialCalibrationRequest,
): Promise<CommercialCalibrationVersion> {
  const { data } = await apiClient.post<CommercialCalibrationVersion>(
    `/api/projects/${projectId}/commercial-calibration/versions`,
    body,
  );
  return data;
}

/**
 * Os erros de regra da recusa, um por campo, ou `null`.
 *
 * A gravação devolve 422 por dois motivos, e só um deles marca campo: a regra da
 * EF (margem fora da faixa, dois cenários marcados...) vem com
 * `code: "invalid_commercial_calibration"`; o corpo malformado vem com a lista do
 * FastAPI, que não diz a posição do cenário.
 */
export function commercialValidationIssues(error: unknown): CommercialValidationIssue[] | null {
  if (detailCode(error) !== "invalid_commercial_calibration") return null;
  const detail = (error as { response?: { data?: { detail?: CommercialValidationError } } })
    .response?.data?.detail;
  return detail?.issues ?? null;
}
